(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MSProjectPlannerAccess = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const LOCAL_MS = 30 * 24 * 60 * 60 * 1000;
  const FRESH_MS = 60 * 60 * 1000;
  const temporary = () => ({ authorized: false, outcome: 'TEMPORARY_ERROR' });
  const positiveDenials = ['REVOKED', 'EXPIRED', 'INVALID'];
  const object = value => value && typeof value === 'object' && !Array.isArray(value);
  const exact = (value, fields) => object(value) && Object.keys(value).length === fields.length &&
    fields.every(field => Object.hasOwn(value, field));
  const reference = value => typeof value === 'string' && value.length > 0 && value.length <= 2048 && !/[\s\u0000-\u001f\u007f]/.test(value);

  function create({ environment, storage, service, now = Date.now,
    geolocation = globalThis.navigator?.geolocation }) {
    if (!['development', 'production'].includes(environment)) throw new Error('Invalid access composition');
    const keys = Object.freeze({local: `ms-planner-access:${environment}:local`, code: `ms-planner-access:${environment}:code`});
    let freshAt = null;
    let localMemory = null;
    let codeMemory = null;
    let generation = 0;
    let pending = null;
    function read(key) {
      try {
        const raw = storage.getItem(key);
        return raw && raw.length <= 4096 ? JSON.parse(raw) : null;
      } catch (_) { return null; }
    }
    function write(key, value) {
      try { storage.setItem(key, JSON.stringify(value)); } catch (_) { /* Page-only fallback; never another persistence channel. */ }
    }
    function remove(key) { try { storage.removeItem(key); } catch (_) { /* Remains denied in this context. */ } }
    function base(record, type) { return record?.schema_version === 1 && record.environment === environment && record.type === type; }
    function localValid(record) {
      const time = now();
      return exact(record, ['schema_version', 'environment', 'type', 'authorized_at', 'expires_at']) && base(record, 'LOCAL_GEOGRAPHIC') &&
        Number.isSafeInteger(record.authorized_at) && Number.isSafeInteger(record.expires_at) &&
        record.authorized_at <= time && record.authorized_at >= 0 && record.expires_at - record.authorized_at === LOCAL_MS && time < record.expires_at;
    }
    function codeValid(record) {
      return exact(record, ['schema_version', 'environment', 'type', 'opaque_revalidation_reference']) &&
        base(record, 'CODE_DERIVED') && reference(record.opaque_revalidation_reference);
    }
    function hasAuthorization() {
      if (localValid(localMemory) || localValid(read(keys.local))) return true;
      return freshAt !== null && now() >= freshAt && now() - freshAt < FRESH_MS;
    }
    function forgetCode() {
      freshAt = null;
      codeMemory = null;
      remove(keys.code);
    }
    async function call(method, value) {
      try { return await service[method](value); } catch (_) { return temporary(); }
    }
    function devicePosition() {
      return new Promise(resolve => {
        let settled = false;
        const finish = value => {
          if (settled) return;
          settled = true;
          resolve(value);
        };
        try {
          if (typeof geolocation?.getCurrentPosition !== 'function') return finish(null);
          // Wait for the browser to resolve location or permission; no Planner deadline.
          geolocation.getCurrentPosition(position => {
            if (settled) return;
            try {
              const {latitude, longitude, accuracy} = position.coords;
              const valid = [latitude, longitude, accuracy].every(value => typeof value === 'number' && Number.isFinite(value)) &&
                Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 && accuracy >= 0;
              finish(valid ? {latitude, longitude, accuracy} : null);
            } catch (_) { finish(null); }
          }, () => finish(null), {enableHighAccuracy: true, maximumAge: 0});
        } catch (_) { finish(null); }
      });
    }
    function credential(result, token, issue) {
      if (generation !== token) return temporary();
      if (result?.outcome === 'ACTIVE' && result.authorized === true && (!issue || reference(result.reference))) {
        if (issue) {
          codeMemory = {schema_version: 1, environment, type: 'CODE_DERIVED', opaque_revalidation_reference: result.reference};
          write(keys.code, codeMemory);
        }
        freshAt = now();
        return {authorized: true, outcome: 'ACTIVE'};
      }
      if (positiveDenials.includes(result?.outcome) && result.authorized === false) {
        forgetCode();
        return {authorized: false, outcome: result.outcome};
      }
      freshAt = null;
      return temporary();
    }
    async function decide(token) {
      if (hasAuthorization()) return {authorized: true, outcome: 'AUTHORIZED'};
      const saved = codeMemory || read(keys.code);
      if (codeValid(saved)) return credential(await call('revalidate', saved.opaque_revalidation_reference), token, false);
      // Malformed references never authorize. Normal geography remains an independent path.
      let result = await call('geography');
      if (generation !== token) return temporary();
      if (result?.outcome === 'OUTSIDE' && result.authorized === false) {
        let position = await devicePosition();
        if (generation !== token) return temporary();
        if (position) {
          const request = call('deviceGeography', position);
          position = null;
          const device = await request;
          if (generation !== token) return temporary();
          if (device?.outcome === 'CLEARLY_INSIDE' && device.authorized === true) result = {outcome: 'INSIDE', authorized: true};
        }
        // Failure to verify device eligibility is not a physical OUTSIDE finding.
        // Retain the original IP fallback and its existing access-code pathway.
      }
      if (result?.outcome === 'INSIDE' && result.authorized === true) {
        const time = now();
        localMemory = {schema_version: 1, environment, type: 'LOCAL_GEOGRAPHIC', authorized_at: time, expires_at: time + LOCAL_MS};
        write(keys.local, localMemory);
        return {authorized: true, outcome: 'INSIDE'};
      }
      if (['OUTSIDE', 'UNKNOWN'].includes(result?.outcome) && result.authorized === false) return {authorized: false, outcome: result.outcome};
      return temporary();
    }
    function authorize() {
      if (!pending) {
        const operation = decide(generation);
        pending = operation;
        operation.finally(() => { if (pending === operation) pending = null; });
      }
      return pending;
    }
    async function validate(code) {
      const token = ++generation;
      pending = null;
      freshAt = null;
      // The code is only an argument to the request; no controller field retains it.
      const request = call('validate', code);
      code = null;
      return credential(await request, token, true);
    }
    // Invalidates an in-flight decision as well as page memory. Storage operations
    // are intentionally outside this primitive and owned by environment composition.
    function invalidatePage() { generation++; pending = null; freshAt = null; localMemory = null; codeMemory = null; }
    return Object.freeze({authorize, validate, hasAuthorization, invalidatePage});
  }

  function transport({ endpoint, fetch: request = globalThis.fetch }) {
    let base;
    try {
      const url = new URL(endpoint);
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash || url.pathname !== '/') throw new Error();
      base = url.origin;
    } catch (_) { base = null; }
    async function send(path, body, kind, credentials = 'omit') {
      if (!base) return temporary();
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 10000);
      try {
        const response = await request(base + path, {method: body === undefined ? 'GET' : 'POST',
          headers: body === undefined ? {} : {'Content-Type': 'application/json'},
          body: body === undefined ? undefined : JSON.stringify(body), credentials,
          cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal: abort.signal});
        const data = await response.json();
        const result = data?.[kind];
        const allowed = kind === 'geography' ? ['INSIDE', 'OUTSIDE', 'UNKNOWN'] :
          kind === 'device_geography' ? ['CLEARLY_INSIDE', 'CLEARLY_OUTSIDE', 'INCONCLUSIVE'] : ['ACTIVE', ...positiveDenials];
        if (data?.version !== 1 || !object(result) || !allowed.includes(result.outcome) ||
            result.authorized !== ['ACTIVE', 'INSIDE', 'CLEARLY_INSIDE'].includes(result.outcome) ||
            !(response.status === 200 || (response.status === 400 && kind === 'credential' && result.outcome === 'INVALID'))) return temporary();
        return {outcome: result.outcome, authorized: result.authorized, ...(data.reference === undefined ? {} : {reference: data.reference})};
      } catch (_) { return temporary(); }
      finally { clearTimeout(timer); }
    }
    return Object.freeze({
      geography: () => send('/v1/geography', undefined, 'geography'),
      deviceGeography: position => send('/v1/geography/device', position, 'device_geography'),
      validate: code => send('/v1/access-code/validate', {code, issue_reference: true}, 'credential'),
      revalidate: reference => send('/v1/access-code/revalidate', {reference}, 'credential'),
    });
  }
  return Object.freeze({create, transport, LOCAL_MS, FRESH_MS});
});
